import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { formatMedida, unidadeLabel } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Loader2, Building2, Layers, Pencil, MessageCircle, Palette, Trash2, Save, X } from "lucide-react";
import { toast } from "sonner";

export default function Cadastro() {
  const { hasPerm } = useAuth();
  const canEditForn = hasPerm("cadastro:fornecedores_editar");
  const canEditComb = hasPerm("cadastro:combinacoes_editar");
  const canViewForn = hasPerm("cadastro:fornecedores_view");
  const canViewComb = hasPerm("cadastro:combinacoes_view");
  const canManageCores = hasPerm("cores:gerenciar");
  const [fornecedores, setFornecedores] = useState([]);
  const [combinacoes, setCombinacoes] = useState([]);
  const [cores, setCores] = useState([]);
  const [novaCor, setNovaCor] = useState("");
  const [savingNovaCor, setSavingNovaCor] = useState(false);
  const [editCor, setEditCor] = useState(null); // {id, nome}
  const [edCorNome, setEdCorNome] = useState("");
  const [savingEdCor, setSavingEdCor] = useState(false);
  const [deletingCor, setDeletingCor] = useState("");

  const [fNome, setFNome] = useState("");
  const [fContato, setFContato] = useState("");
  const [savingF, setSavingF] = useState(false);

  const [cTipo, setCTipo] = useState("");
  const [cCor, setCCor] = useState("");
  const [cForn, setCForn] = useState("");
  const [cMin, setCMin] = useState("");
  const [cUnidade, setCUnidade] = useState("kg");
  const [savingC, setSavingC] = useState(false);

  const [editComb, setEditComb] = useState(null);
  const [edTipo, setEdTipo] = useState("");
  const [edCor, setEdCor] = useState("");
  const [edForn, setEdForn] = useState("");
  const [edMin, setEdMin] = useState("");
  const [edUnidade, setEdUnidade] = useState("kg");
  const [savingE, setSavingE] = useState(false);

  const [editForn, setEditForn] = useState(null);
  const [efNome, setEfNome] = useState("");
  const [efContato, setEfContato] = useState("");
  const [savingEf, setSavingEf] = useState(false);

  const load = () => {
    api.get("/fornecedores").then((r) => setFornecedores(r.data)).catch((e) => {const _m=apiError(e); if(_m) toast.error(_m);});
    api.get("/combinacoes").then((r) => setCombinacoes(r.data)).catch((e) => {const _m=apiError(e); if(_m) toast.error(_m);});
    api.get("/cores").then((r) => setCores(r.data)).catch((e) => {const _m=apiError(e); if(_m) toast.error(_m);});
  };

  useEffect(load, []);

  // ── Cores ────────────────────────────────────────────────────
  const addCor = async (e) => {
    e.preventDefault();
    const nome = novaCor.trim();
    if (!nome) return;
    setSavingNovaCor(true);
    try {
      await api.post("/cores", { nome });
      toast.success(`Cor "${nome}" cadastrada`);
      setNovaCor("");
      load();
    } catch (e) { const _m = apiError(e); if (_m) toast.error(_m); }
    finally { setSavingNovaCor(false); }
  };
  const startEditCor = (c) => { setEditCor(c); setEdCorNome(c.nome); };
  const saveEditCor = async () => {
    const nome = edCorNome.trim();
    if (!nome) { toast.error("Nome não pode ficar vazio"); return; }
    setSavingEdCor(true);
    try {
      await api.put(`/cores/${editCor.id}`, { nome });
      toast.success("Cor atualizada");
      setEditCor(null);
      load();
    } catch (e) { const _m = apiError(e); if (_m) toast.error(_m); }
    finally { setSavingEdCor(false); }
  };
  const removeCor = async (c) => {
    if (!window.confirm(`Excluir a cor "${c.nome}"?`)) return;
    setDeletingCor(c.id);
    try {
      await api.delete(`/cores/${c.id}`);
      toast.success("Cor excluída");
      load();
    } catch (e) { const _m = apiError(e); if (_m) toast.error(_m); }
    finally { setDeletingCor(""); }
  };

  const addFornecedor = async (e) => {
    e.preventDefault();
    setSavingF(true);
    try {
      await api.post("/fornecedores", { nome: fNome, contato: fContato });
      toast.success("Fornecedor cadastrado");
      setFNome("");
      setFContato("");
      load();
    } catch (err) {
      {const _m=apiError(err); if(_m) toast.error(_m);};
    } finally {
      setSavingF(false);
    }
  };

  const addCombinacao = async (e) => {
    e.preventDefault();
    if (!cForn) {
      toast.error("Selecione um fornecedor");
      return;
    }
    setSavingC(true);
    try {
      await api.post("/combinacoes", {
        tipo: cTipo,
        cor: cCor,
        fornecedor_id: cForn,
        estoque_minimo_kg: parseFloat(cMin) || 0,
        unidade: cUnidade,
      });
      toast.success("Combinação de tecido cadastrada");
      setCTipo("");
      setCCor("");
      setCMin("");
      load();
    } catch (err) {
      {const _m=apiError(err); if(_m) toast.error(_m);};
    } finally {
      setSavingC(false);
    }
  };

  const openEdit = (c) => {
    setEditComb(c);
    setEdTipo(c.tipo);
    setEdCor(c.cor);
    setEdForn(c.fornecedor_id);
    setEdMin(String(c.estoque_minimo_kg).replace(".", ","));
    setEdUnidade(c.unidade || "kg");
  };

  const saveEdit = async () => {
    if (!edTipo.trim() || !edCor.trim()) {
      toast.error("Tipo e cor são obrigatórios");
      return;
    }
    setSavingE(true);
    try {
      await api.put(`/combinacoes/${editComb.id}`, {
        tipo: edTipo,
        cor: edCor,
        fornecedor_id: edForn,
        estoque_minimo_kg: parseFloat(String(edMin).replace(",", ".")) || 0,
        unidade: edUnidade,
      });
      toast.success("Tecido atualizado");
      setEditComb(null);
      load();
    } catch (err) {
      {const _m=apiError(err); if(_m) toast.error(_m);};
    } finally {
      setSavingE(false);
    }
  };

  const openEditForn = (f) => {
    setEditForn(f);
    setEfNome(f.nome);
    setEfContato(f.contato || "");
  };

  const saveEditForn = async () => {
    if (!efNome.trim()) {
      toast.error("Nome do fornecedor é obrigatório");
      return;
    }
    setSavingEf(true);
    try {
      await api.put(`/fornecedores/${editForn.id}`, { nome: efNome, contato: efContato });
      toast.success("Fornecedor atualizado");
      setEditForn(null);
      load();
    } catch (err) {
      {const _m=apiError(err); if(_m) toast.error(_m);};
    } finally {
      setSavingEf(false);
    }
  };

  const waLink = (contato) => {
    const digits = String(contato || "").replace(/\D/g, "");
    if (!digits) return null;
    const num = digits.length <= 11 ? `55${digits}` : digits;
    return `https://wa.me/${num}`;
  };

  const inputCls =
    "h-12 w-full rounded-md border-2 border-slate-300 px-3 text-base outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200";

  return (
    <div data-testid="cadastro-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">Cadastro</h1>
        <p className="mt-1 text-sm text-slate-500">Fornecedores e combinações de tecido (Tipo + Cor + Fornecedor).</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Fornecedores */}
        {canViewForn && (
        <section className="rounded-md border-2 border-slate-200 bg-white p-5">
          <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
            <Building2 size={20} /> Fornecedores
          </h2>
          {canEditForn && (
          <form onSubmit={addFornecedor} className="mt-4 space-y-3" data-testid="fornecedor-form">
            <input
              className={inputCls}
              placeholder="Nome do fornecedor (ex: XT, ITEMA)"
              value={fNome}
              onChange={(e) => setFNome(e.target.value)}
              data-testid="fornecedor-nome"
              required
            />
            <input
              className={inputCls}
              placeholder="Contato / WhatsApp (opcional)"
              value={fContato}
              onChange={(e) => setFContato(e.target.value)}
              data-testid="fornecedor-contato"
            />
            <button
              type="submit"
              disabled={savingF}
              data-testid="fornecedor-submit"
              className="flex h-12 w-full items-center justify-center gap-2 rounded-md bg-slate-900 font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {savingF ? <Loader2 className="animate-spin" size={18} /> : <Plus size={18} />} Adicionar fornecedor
            </button>
          </form>
          )}

          <ul className="mt-4 divide-y divide-slate-100" data-testid="fornecedor-list">
            {fornecedores.length === 0 && <li className="py-3 text-sm text-slate-400">Nenhum fornecedor ainda.</li>}
            {fornecedores.map((f) => (
              <li key={f.id} className="flex items-center justify-between gap-2 py-3" data-testid={`fornecedor-item-${f.id}`}>
                <div className="min-w-0">
                  <span className="font-semibold text-slate-800">{f.nome}</span>
                  {f.contato && <span className="ml-2 text-sm text-slate-500">{f.contato}</span>}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {waLink(f.contato) && (
                    <a
                      href={waLink(f.contato)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`fornecedor-whatsapp-${f.id}`}
                      title="Falar no WhatsApp"
                      className="flex items-center gap-1 rounded-md border-2 border-green-200 bg-green-50 px-2.5 py-1.5 text-xs font-semibold text-green-700 hover:bg-green-100"
                    >
                      <MessageCircle size={14} /> WhatsApp
                    </a>
                  )}
                  {canEditForn && (
                    <button
                      onClick={() => openEditForn(f)}
                      data-testid={`fornecedor-edit-${f.id}`}
                      title="Editar fornecedor"
                      className="rounded-md border-2 border-slate-200 p-2 text-slate-600 hover:bg-slate-100"
                    >
                      <Pencil size={15} />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
        )}

        {/* Combinações */}
        {canViewComb && (
        <section className="rounded-md border-2 border-slate-200 bg-white p-5">
          <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
            <Layers size={20} /> Tecidos (Tipo + Cor + Fornecedor)
          </h2>
          {canEditComb && (
          <form onSubmit={addCombinacao} className="mt-4 space-y-3" data-testid="combinacao-form">
            <div className="grid grid-cols-2 gap-3">
              <input
                className={inputCls}
                placeholder="Tipo (ex: Piquet)"
                value={cTipo}
                onChange={(e) => setCTipo(e.target.value)}
                data-testid="combinacao-tipo"
                required
              />
              <input
                className={inputCls}
                placeholder="Cor (ex: Preto)"
                value={cCor}
                onChange={(e) => setCCor(e.target.value)}
                data-testid="combinacao-cor"
                required
              />
            </div>
            <select
              className={inputCls}
              value={cForn}
              onChange={(e) => setCForn(e.target.value)}
              data-testid="combinacao-fornecedor"
              required
            >
              <option value="">Selecione o fornecedor</option>
              {fornecedores.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.nome}
                </option>
              ))}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <select
                className={inputCls}
                value={cUnidade}
                onChange={(e) => setCUnidade(e.target.value)}
                data-testid="combinacao-unidade"
              >
                <option value="kg">Controle em KG (peso)</option>
                <option value="m">Controle em METRO</option>
              </select>
              <input
                type="number"
                step="0.01"
                min="0"
                className={inputCls}
                placeholder={`Estoque mínimo (${cUnidade === "m" ? "m" : "kg"})`}
                value={cMin}
                onChange={(e) => setCMin(e.target.value)}
                data-testid="combinacao-minimo"
              />
            </div>
            <button
              type="submit"
              disabled={savingC}
              data-testid="combinacao-submit"
              className="flex h-12 w-full items-center justify-center gap-2 rounded-md bg-slate-900 font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {savingC ? <Loader2 className="animate-spin" size={18} /> : <Plus size={18} />} Adicionar tecido
            </button>
          </form>
          )}

          <ul className="mt-4 divide-y divide-slate-100" data-testid="combinacao-list">
            {combinacoes.length === 0 && <li className="py-3 text-sm text-slate-400">Nenhuma combinação ainda.</li>}
            {combinacoes.map((c) => (
              <li key={c.id} className="flex items-center justify-between py-3" data-testid={`combinacao-item-${c.id}`}>
                <div>
                  <span className="font-semibold text-slate-800">
                    {c.tipo} {c.cor}
                  </span>
                  <span className="ml-2 text-sm text-slate-500">{c.fornecedor_nome}</span>
                  <span className="ml-2 rounded-sm bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-600">
                    {unidadeLabel(c.unidade)}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-medium text-slate-400">
                    mín. {formatMedida(c.estoque_minimo_kg, c.unidade)}
                  </span>
                  {canEditComb && (
                    <button
                      onClick={() => openEdit(c)}
                      data-testid={`combinacao-edit-${c.id}`}
                      className="rounded-md border-2 border-slate-200 p-2 text-slate-600 hover:bg-slate-100"
                    >
                      <Pencil size={15} />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
        )}
      </div>

      {/* Cores — nova seção (usada pelo Estoque de Gola e Punho) */}
      {canManageCores && (
      <section className="mt-6 rounded-md border-2 border-slate-200 bg-white p-5" data-testid="cores-section">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
          <Palette size={20} /> Cores
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Cores usadas pelos produtos do Estoque de Gola e Punho. Renomear reflete em todos os produtos que usam a cor. Só é possível excluir se nenhum produto estiver usando.
        </p>

        <form onSubmit={addCor} className="mt-4 flex flex-wrap items-center gap-2" data-testid="cor-form">
          <input
            className={`${inputCls} flex-1 min-w-[12rem]`}
            placeholder="Nome da cor (ex: Preto, Azul Bebê)"
            value={novaCor}
            onChange={(e) => setNovaCor(e.target.value)}
            data-testid="cor-nova-input"
            disabled={savingNovaCor}
          />
          <button
            type="submit"
            disabled={savingNovaCor || !novaCor.trim()}
            data-testid="cor-nova-add"
            className="flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {savingNovaCor ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Cadastrar cor
          </button>
        </form>

        {cores.length === 0 ? (
          <div className="mt-4 rounded-md border-2 border-dashed border-slate-300 p-6 text-center text-sm text-slate-500" data-testid="cores-empty">
            Nenhuma cor cadastrada ainda.
          </div>
        ) : (
          <ul className="mt-4 divide-y divide-slate-100" data-testid="cores-list">
            {cores.map((c) => (
              <li key={c.id} className="flex items-center justify-between py-3" data-testid={`cor-item-${c.id}`}>
                {editCor?.id === c.id ? (
                  <>
                    <input
                      className={`${inputCls} flex-1 mr-2`}
                      value={edCorNome}
                      onChange={(e) => setEdCorNome(e.target.value)}
                      data-testid={`cor-edit-input-${c.id}`}
                      autoFocus
                    />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={saveEditCor}
                        disabled={savingEdCor}
                        data-testid={`cor-edit-save-${c.id}`}
                        className="flex items-center gap-1 rounded-md bg-slate-900 px-3 py-2 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50"
                      >
                        {savingEdCor ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        Salvar
                      </button>
                      <button
                        onClick={() => setEditCor(null)}
                        className="rounded-md border-2 border-slate-200 p-2 text-slate-600 hover:bg-slate-100"
                        data-testid={`cor-edit-cancel-${c.id}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="font-semibold text-slate-800" data-testid={`cor-nome-${c.id}`}>{c.nome}</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => startEditCor(c)}
                        className="rounded-md border-2 border-slate-200 p-2 text-slate-600 hover:bg-slate-100"
                        data-testid={`cor-edit-${c.id}`}
                        title="Renomear"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => removeCor(c)}
                        disabled={deletingCor === c.id}
                        className="rounded-md border-2 border-red-200 p-2 text-red-600 hover:bg-red-50 disabled:opacity-50"
                        data-testid={`cor-remove-${c.id}`}
                        title="Excluir"
                      >
                        {deletingCor === c.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
      )}

      <Dialog open={!!editComb} onOpenChange={(o) => !o && setEditComb(null)}>
        <DialogContent data-testid="combinacao-edit-dialog">
          <DialogHeader>
            <DialogTitle>
              Editar {editComb?.tipo} {editComb?.cor} — {editComb?.fornecedor_nome}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">Tipo</label>
                <input className={inputCls} value={edTipo} onChange={(e) => setEdTipo(e.target.value)} data-testid="edit-comb-tipo" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">Cor</label>
                <input className={inputCls} value={edCor} onChange={(e) => setEdCor(e.target.value)} data-testid="edit-comb-cor" />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Fornecedor</label>
              <select className={inputCls} value={edForn} onChange={(e) => setEdForn(e.target.value)} data-testid="edit-comb-fornecedor">
                {fornecedores.map((f) => (
                  <option key={f.id} value={f.id}>{f.nome}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Unidade</label>
              <select className={inputCls} value={edUnidade} onChange={(e) => setEdUnidade(e.target.value)} data-testid="edit-comb-unidade">
                <option value="kg">Controle em KG (peso)</option>
                <option value="m">Controle em METRO</option>
              </select>
              <p className="mt-1 text-xs text-slate-400">A unidade só pode mudar enquanto o tecido não tiver nenhum rolo cadastrado.</p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Estoque mínimo ({unidadeLabel(edUnidade)})
              </label>
              <input className={inputCls} value={edMin} onChange={(e) => setEdMin(e.target.value)} inputMode="decimal" data-testid="edit-comb-minimo" />
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={saveEdit}
              disabled={savingE}
              data-testid="edit-comb-save"
              className="flex h-12 w-full items-center justify-center gap-2 rounded-md bg-slate-900 font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {savingE && <Loader2 className="animate-spin" size={18} />} Salvar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editForn} onOpenChange={(o) => !o && setEditForn(null)}>
        <DialogContent data-testid="fornecedor-edit-dialog">
          <DialogHeader>
            <DialogTitle>Editar fornecedor</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Nome</label>
              <input className={inputCls} value={efNome} onChange={(e) => setEfNome(e.target.value)} data-testid="edit-forn-nome" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Contato / WhatsApp</label>
              <input className={inputCls} value={efContato} onChange={(e) => setEfContato(e.target.value)} data-testid="edit-forn-contato" placeholder="Opcional" />
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={saveEditForn}
              disabled={savingEf}
              data-testid="edit-forn-save"
              className="flex h-12 w-full items-center justify-center gap-2 rounded-md bg-slate-900 font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {savingEf && <Loader2 className="animate-spin" size={18} />} Salvar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
